import { useEffect } from 'react';
import { waitForNotesUpdate } from './noteFetchUtils';

// One cancellable request chain per owner. A delay also bounds retries when a
// server/proxy returns immediately instead of holding the long poll.
export default function useNotesPolling(refresh, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    let controller;
    let timer;
    let version = 0;
    const stop = () => {
      controller?.abort();
      clearTimeout(timer);
    };
    const start = () => {
      stop();
      if (document.visibilityState !== 'visible') return;
      controller = new AbortController();
      const { signal } = controller;
      const poll = async () => {
        let delay = 1000;
        try {
          const result = await waitForNotesUpdate({
            sinceVersion: version, timeoutSec: 55, signal,
          });
          if (signal.aborted) return;
          if (Number.isFinite(result?.latestVersion)) version = result.latestVersion;
          // Reconcile even on timeout: a missed/version-coalesced notification
          // must not strand a late document.
          await refresh({ signal });
        } catch (error) {
          if (signal.aborted) return;
          console.error('Note update failed, retrying:', error);
          delay = 3000;
        }
        if (!signal.aborted) timer = setTimeout(poll, delay);
      };
      const run = async () => {
        await refresh({ signal });
        if (!signal.aborted) poll();
      };
      run();
    };
    start();
    document.addEventListener('visibilitychange', start);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', start);
    };
  }, [refresh, enabled]);
}
